import { Box, Grid, GridItem, Stack, useBreakpointValue } from "@chakra-ui/react";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";

import { usePostDevFavoritesMutation } from "@/hooks/microservicesApi";

import Container from "../general/Container";
import DevelopsNear from "./DevelopsNear";
import Amenities from "./modules/Amenities";
import CentralBanner from "./modules/CentralBanner";
import CreditSimulator from "./modules/CreditSimulator";
import Description from "./modules/Description";
import FormDate from "./modules/FormDate";
import FormModal from "./modules/FormModal";
import Location from "./modules/Location";
import MediaVideo from "./modules/MediaVideo";
import Opinions from "./modules/Opinions";
import VirtualTour from "./modules/VirtualTour";

const Desarrollo = ({ city, dataDevelopment, listDevs, favorites, opinions, setIsRouteViewer }) => {
    //Obtener datos de sesión
    const { data: session } = useSession();

    //Inicializar los favoritos, declarar Set para actualizar la lista con los datos actualizados.
    const [userFavorites, setUserFavorites] = useState(favorites || []);

    //Declarar el estado del modal
    const [isOpen, setIsOpen] = useState(false);

    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);

    //Declarar fecha y hora para tarjeta de contacto y modal
    const [date, setDate] = useState("");
    const [time, setTime] = useState("08:00");

    //Declarar el origen del modal (Contacto a formulario de whatsapp o de contacto)
    const [formOrigin, setFormOrigin] = useState("contact");

    //Comprobar si el tamaño de pantalla es mayor a MD
    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });
    const isGreaterThanLg = useBreakpointValue({ base: false, lg: true });

    //Inicializar el endpoint para guardado de Favoritos
    const [postDevFavorites] = usePostDevFavoritesMutation();

    // Desaparecer el FormDate al llegar al Carrusel DevelopersNear
    const DevelopersNear = useRef(null);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const isVisibleDevelopersNear = DevelopersNear.current;

        if (!isVisibleDevelopersNear) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Si el tercer GridItem está visible, oculta el segundo
                    if (entry.isIntersecting) {
                        setIsVisible(false);
                    } else {
                        setIsVisible(true);
                    }
                });
            },
            { threshold: 0.85 }
        ); // Detecta cuando al menos el 85% del elemento es visible

        observer.observe(isVisibleDevelopersNear);

        return () => {
            if (isVisibleDevelopersNear) {
                observer.unobserve(isVisibleDevelopersNear);
            }
        };
    }, []);

    //Hacer fixed el Box para Formulario de citas (FormDate)
    useEffect(() => {
        const handleScroll = () => {
            const formContainer = document.getElementById("form-container");
            const stickyForm = document.querySelector(".form-sticky-container");

            if (formContainer && stickyForm) {
                const containerRect = formContainer.getBoundingClientRect();

                // Si el contenedor está dentro del viewport (visible)
                if (containerRect.top < window.innerHeight && containerRect.bottom > 0) {
                    // Calcula la posición visible del contenedor
                    let visibleTop = Math.max(0, containerRect.top);

                    // Agrega 100px de margen con respecto al header
                    visibleTop = Math.max(100, visibleTop);

                    stickyForm.style.position = "fixed";
                    stickyForm.style.top = `${visibleTop}px`;
                    stickyForm.style.width = `${containerRect.width}px`;
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        // Ejecutar una vez al cargar para establecer la posición inicial
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    //Función para formatear precios a MXN
    const formatPrice = (amount) => {
        return new Intl.NumberFormat("es-MX", {
            style: "currency",
            currency: "MXN",
            minimumFractionDigits: isGreaterThanMd ? 2 : 0,
            maximumFractionDigits: isGreaterThanMd ? 2 : 0,
        }).format(amount);
    };

    //Función para añadir favoritos a la base de datos
    const saveDevFavorites = async (propertyId, devId) => {
        try {
            const response = await postDevFavorites({
                body: {
                    contact_id: session?.contact_id,
                    property_id: propertyId || null,
                    development_id: devId || null,
                    changed: new Date().toISOString(),
                },
                token: session?.token,
            }).unwrap();
            setUserFavorites(response?.favorites);
        } catch (error) {
            console.error("Error al guardar favorito:", error);
            if (error?.status === 401) {
                getSession().then((data) => {
                    if (data?.error === "RefreshAccessTokenError") {
                        console.warn("Token refresh failed. Redirecting to login...");
                        return signIn("keycloak");
                    }
                });
            }
        }
    };

    return dataDevelopment ? (
        <>
            <Stack p={{ base: 5, md: 12 }} position="relative" zIndex={0}>
                <CentralBanner
                    dataDevelopment={dataDevelopment}
                    city={city}
                    session={session}
                    saveDevFavorites={saveDevFavorites}
                    userFavorites={userFavorites}
                    setIsRouteViewer={setIsRouteViewer}
                />
                <Grid
                    templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(3, 1fr)" }}
                    gap={2}
                    pt={4}
                >
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                        <Stack>
                            <Description
                                dataDevelopment={dataDevelopment}
                                isGreaterThanMd={isGreaterThanMd}
                                formatPrice={formatPrice}
                            />
                            <Amenities dataDevelopment={dataDevelopment} />
                            <Location
                                dataDevelopment={dataDevelopment}
                                isGreaterThanMd={isGreaterThanMd}
                            />
                            <Opinions dataDevelopment={dataDevelopment} opinions={opinions} />
                            <CreditSimulator
                                dataDevelopment={dataDevelopment}
                                isGreaterThanLg={isGreaterThanLg}
                                formatPrice={formatPrice}
                                session={session}
                                onOpen={onOpen}
                                setFormOrigin={setFormOrigin}
                            />
                            <VirtualTour dataDevelopment={dataDevelopment} />
                            <MediaVideo dataDevelopment={dataDevelopment} />
                        </Stack>
                    </GridItem>
                    {isVisible && isGreaterThanLg && (
                        <GridItem colSpan={1} position="relative" id="form-container">
                            <Box position="sticky" top="0" className="form-sticky-container">
                                <FormDate
                                    dataDevelopment={dataDevelopment}
                                    formatPrice={formatPrice}
                                    isGreaterThanLg={isGreaterThanLg}
                                    onOpen={onOpen}
                                    setFormOrigin={setFormOrigin}
                                    date={date}
                                    setDate={setDate}
                                    time={time}
                                    setTime={setTime}
                                />
                            </Box>
                        </GridItem>
                    )}
                    <GridItem colSpan={{ base: 1, md: 3 }} ref={DevelopersNear}>
                        <DevelopsNear
                            code_dev={dataDevelopment?.wiggot_code}
                            listDevs={listDevs}
                            session={session}
                            saveDevFavorites={saveDevFavorites}
                            userFavorites={userFavorites}
                        />
                    </GridItem>
                </Grid>
            </Stack>
            {!isGreaterThanLg && (
                <Box zIndex={99}>
                    <FormDate
                        dataDevelopment={dataDevelopment}
                        formatPrice={formatPrice}
                        isGreaterThanLg={isGreaterThanLg}
                        onOpen={onOpen}
                        setFormOrigin={setFormOrigin}
                        date={date}
                        setDate={setDate}
                        time={time}
                        setTime={setTime}
                    />
                </Box>
            )}

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                origin={formOrigin}
                developmentName={dataDevelopment?.name}
                date={date}
                setDate={setDate}
                time={time}
                setTime={setTime}
                formIds={dataDevelopment?.external_props?.hubspot?.forms}
                wiggot_props={dataDevelopment?.wiggot_props}
            />
        </>
    ) : (
        <Container
            maxW={{ base: "md", md: "6xl" }}
            paddingTop={{ base: "50px", md: "100px" }}
            paddingBottom={{ base: "50px", md: "40px" }}
            marginY={{ base: 18, md: 24 }}
        >
            <h2>Lo sentimos, desarrollo no localizado.</h2>
        </Container>
    );
};

export default Desarrollo;
